import { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import { DownOutlined, RightOutlined } from "@ant-design/icons"
import { Alert, Button, Modal, Select, Space, Tag, Typography, message } from "antd"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import CreateMeetPoolModal from "./CreateMeetPoolModal"
import { gen, IClubSeason, swimminglyApi } from "./utils"

const { Text, Paragraph } = Typography
const EMPTY_INVITED_TEAMS: IVirtualMeetParticipantOverview[] = []
const ACTIVITY_FEED_REFETCH_EVENT = "swimmingly:activity-feed:refetch"

interface IPoolOption {
  poolId: number
  pool_name: string
  course_label: "SCY" | "SCM" | "LCM" | "CUSTOM" | null
  address?: string | null
}

interface IPendingVirtualMeetInvitationContext {
  virtualMeetInvitationId: number
  virtualMeetId: number
  organizerClubId: number
}

interface IVirtualMeetParticipantOverview {
  virtualMeetInvitationId: number
  virtualMeetId: number
  clubId: number
  status: "accepted" | "pending" | "declined" | "removed"
  clubName: string | null
  clubNameLong: string | null
}

interface IVirtualMeetInvitationResponseModalProps {
  visible: boolean
  setVisible: (newVisible: boolean) => void
  virtualMeetInvitationId: number | null
  clubId: number | null
  virtualMeetId?: number | null
  initialOrganizerClubId?: number | null
  initialInvitedTeams?: IVirtualMeetParticipantOverview[]
  initialSeasonId?: number | null
  title?: string
  description?: string
  allowDecline?: boolean
  onSuccess?: () => void
}

export default function VirtualMeetInvitationResponseModal({
  visible,
  setVisible,
  virtualMeetInvitationId,
  clubId,
  virtualMeetId = null,
  initialOrganizerClubId = null,
  initialInvitedTeams = EMPTY_INVITED_TEAMS,
  initialSeasonId = null,
  title = "Configure Virtual Meet Invitation",
  description = "Choose the season roster and pool your club will use for this virtual meet.",
  allowDecline = true,
  onSuccess,
}: IVirtualMeetInvitationResponseModalProps): JSX.Element {
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const aliasedUser = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedUser,
  )
  const actingClub = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedClub,
  )
  const poolCreateBlockedReason =
    user?.role === 2 && (!aliasedUser || aliasedUser.clubId !== clubId)
      ? "Swimmingly admins can add pools from meet setup only when aliased into an eligible club user for this club. Switch into that club user first, then try again."
      : null
  const [submitting, setSubmitting] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [clubSeasons, setClubSeasons] = useState<IClubSeason[]>([])
  const [poolOptions, setPoolOptions] = useState<IPoolOption[]>([])
  const [createPoolModalVisible, setCreatePoolModalVisible] = useState(false)
  const [selectedSeasonId, setSelectedSeasonId] = useState<number | null>(
    initialSeasonId,
  )
  const [selectedPoolId, setSelectedPoolId] = useState<number | null>(null)
  const [invitedTeamsExpanded, setInvitedTeamsExpanded] = useState(false)
  const [invitedTeamsLoading, setInvitedTeamsLoading] = useState(false)
  const [organizerClubId, setOrganizerClubId] = useState<number | null>(null)
  const [invitedTeams, setInvitedTeams] = useState<IVirtualMeetParticipantOverview[]>([])

  const canCreatePoolForInvitationClub =
    clubId !== null && actingClub?.clubId === clubId

  const loadClubSetupOptions = async () => {
    if (!clubId || !virtualMeetInvitationId) {
      setClubSeasons([])
      setPoolOptions([])
      return { seasons: [], pools: [] }
    }

    const [seasonData, poolData] = await Promise.all([
      swimminglyApi.get(gen(`/api/getSeasonsForClub/${clubId}`)),
      swimminglyApi.get(gen(`/api/getClubPools/${clubId}`)),
    ])

    const availableSeasons =
      seasonData?.status === "success"
        ? (seasonData.clubSeasons as IClubSeason[]).filter((clubSeason) => {
            if (clubSeason.seasonId === initialSeasonId) {
              return true
            }

            return !dayjs(clubSeason.endDate).endOf("day").isBefore(dayjs())
          })
        : []

    const availablePools =
      poolData?.status === "success" ? ((poolData.pools || []) as IPoolOption[]) : []

    setClubSeasons(availableSeasons)
    setPoolOptions(availablePools)
    setSelectedSeasonId((currentSeasonId) => {
      if (
        currentSeasonId &&
        availableSeasons.some((clubSeason) => clubSeason.seasonId === currentSeasonId)
      ) {
        return currentSeasonId
      }

      if (
        initialSeasonId &&
        availableSeasons.some((clubSeason) => clubSeason.seasonId === initialSeasonId)
      ) {
        return initialSeasonId
      }

      return availableSeasons[0]?.seasonId || null
    })
    setSelectedPoolId((currentPoolId) => {
      if (currentPoolId && availablePools.some((pool) => pool.poolId === currentPoolId)) {
        return currentPoolId
      }

      return availablePools[0]?.poolId || null
    })

    return { seasons: availableSeasons, pools: availablePools }
  }

  useEffect(() => {
    if (!visible) {
      setSelectedSeasonId(initialSeasonId)
      setSelectedPoolId(null)
      setCreatePoolModalVisible(false)
      setInvitedTeamsExpanded(false)
      setInvitedTeamsLoading(false)
      setOrganizerClubId(initialOrganizerClubId)
      setInvitedTeams(initialInvitedTeams)
      return
    }

    if (!clubId || !virtualMeetInvitationId) {
      return
    }

    let mounted = true
    setLoadingOptions(true)

    loadClubSetupOptions()
      .then(() => {
        if (!mounted) {
          return
        }
      })
      .catch(() => {
        if (mounted) {
          setClubSeasons([])
          setPoolOptions([])
          message.error("Problem loading club setup options for this invitation.")
        }
      })
      .finally(() => {
        if (mounted) {
          setLoadingOptions(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [visible, clubId, virtualMeetInvitationId, initialSeasonId])

  useEffect(() => {
    if (!visible || !clubId || !virtualMeetInvitationId) {
      return
    }

    let mounted = true
    setInvitedTeamsLoading(true)

    const loadParticipantsOverview = (participantsVirtualMeetId: number) =>
      swimminglyApi
        .get(gen(`/api/getVirtualMeetParticipantsOverview/${participantsVirtualMeetId}`))
        .then(
          (participantsData: {
            status?: string
            organizerClubId?: number
            participants?: IVirtualMeetParticipantOverview[]
          }) => {
            if (!mounted) {
              return
            }

            setOrganizerClubId(participantsData?.organizerClubId || null)
            setInvitedTeams(participantsData?.participants || [])
          },
        )

    const participantsRequest =
      virtualMeetId && Number.isInteger(virtualMeetId)
        ? initialInvitedTeams.length > 0
          ? Promise.resolve().then(() => {
              if (!mounted) {
                return
              }

              setOrganizerClubId(initialOrganizerClubId)
              setInvitedTeams(initialInvitedTeams)
            })
          : loadParticipantsOverview(virtualMeetId)
        : swimminglyApi
            .get(gen(`/api/getPendingVirtualMeetInvitations`))
            .then(
              (
                invitationData: {
                  status?: string
                  invitations?: IPendingVirtualMeetInvitationContext[]
                },
              ) => {
                if (!mounted) {
                  return
                }

                const matchingInvitation = (invitationData?.invitations || []).find(
                  (invitation) =>
                    invitation.virtualMeetInvitationId === virtualMeetInvitationId,
                )

                if (!matchingInvitation?.virtualMeetId) {
                  setOrganizerClubId(null)
                  setInvitedTeams([])
                  return
                }

                return loadParticipantsOverview(matchingInvitation.virtualMeetId)
              },
            )

    participantsRequest
      .catch(() => {
        if (mounted) {
          setOrganizerClubId(null)
          setInvitedTeams([])
        }
      })
      .finally(() => {
        if (mounted) {
          setInvitedTeamsLoading(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [
    visible,
    clubId,
    virtualMeetInvitationId,
    virtualMeetId,
    initialOrganizerClubId,
    initialInvitedTeams,
  ])

  const selectedSeason = useMemo(
    () =>
      clubSeasons.find((clubSeason) => clubSeason.seasonId === selectedSeasonId) || null,
    [clubSeasons, selectedSeasonId],
  )

  const hasAvailableSeason = clubSeasons.length > 0
  const hasAvailablePool = poolOptions.length > 0

  const orderedInvitedTeams = useMemo(() => {
    const alphabeticalTeams = [...invitedTeams].sort((left, right) => {
      const leftName = left.clubNameLong || left.clubName || `Club ${left.clubId}`
      const rightName = right.clubNameLong || right.clubName || `Club ${right.clubId}`

      return leftName.localeCompare(rightName)
    })

    const prioritizedClubIds = new Set<number>()
    const orderedTeams: IVirtualMeetParticipantOverview[] = []

    if (organizerClubId) {
      const organizerTeam = alphabeticalTeams.find(
        (team) => team.clubId === organizerClubId,
      )

      if (organizerTeam) {
        orderedTeams.push(organizerTeam)
        prioritizedClubIds.add(organizerTeam.clubId)
      }
    }

    if (clubId) {
      const myTeam = alphabeticalTeams.find((team) => team.clubId === clubId)

      if (myTeam && !prioritizedClubIds.has(myTeam.clubId)) {
        orderedTeams.push(myTeam)
        prioritizedClubIds.add(myTeam.clubId)
      }
    }

    return orderedTeams.concat(
      alphabeticalTeams.filter((team) => !prioritizedClubIds.has(team.clubId)),
    )
  }, [clubId, invitedTeams, organizerClubId])

  const acceptInvitation = async () => {
    if (!virtualMeetInvitationId) {
      message.error("Missing virtual meet invitation.")
      return
    }

    if (!selectedSeasonId) {
      message.error("Please choose a season roster.")
      return
    }

    if (!selectedPoolId) {
      message.error("Please choose a pool.")
      return
    }

    setSubmitting(true)
    try {
      const response = await swimminglyApi
        .post(gen(`/api/acceptVirtualMeetInvitation`))
        .body({
          virtualMeetInvitationId,
          seasonId: selectedSeasonId,
          poolId: selectedPoolId,
          effectiveUserId: user?.userId || null,
        })

      if (response?.status !== "success") {
        message.error(response?.message || "Problem accepting virtual meet invitation.")
        return
      }

      message.success("Virtual meet setup saved.")
      window.dispatchEvent(new Event(ACTIVITY_FEED_REFETCH_EVENT))
      setVisible(false)
      onSuccess?.()
    } catch (err) {
      message.error("Problem accepting virtual meet invitation.")
    } finally {
      setSubmitting(false)
    }
  }

  const declineInvitation = async () => {
    if (!virtualMeetInvitationId) {
      message.error("Missing virtual meet invitation.")
      return
    }

    setSubmitting(true)
    try {
      const response = await swimminglyApi
        .post(gen(`/api/declineVirtualMeetInvitation`))
        .body({
          virtualMeetInvitationId,
          effectiveUserId: user?.userId || null,
        })

      if (response?.status !== "success") {
        message.error(response?.message || "Problem declining virtual meet invitation.")
        return
      }

      message.success("Virtual meet invitation declined.")
      window.dispatchEvent(new Event(ACTIVITY_FEED_REFETCH_EVENT))
      setVisible(false)
      onSuccess?.()
    } catch (err) {
      message.error("Problem declining virtual meet invitation.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={() => setVisible(false)}
      destroyOnHidden
      footer={[
        allowDecline ? (
          <Button
            key="decline"
            danger
            onClick={declineInvitation}
            loading={submitting}
          >
            Decline Invitation
          </Button>
        ) : null,
        <Button key="cancel" onClick={() => setVisible(false)} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="accept"
          type="primary"
          onClick={acceptInvitation}
          loading={submitting}
          disabled={loadingOptions || !hasAvailableSeason || !hasAvailablePool}
        >
          Save My Club Setup
        </Button>,
      ]}
    >
      <Space orientation="vertical" size={16} style={{ width: "100%" }}>
        <Paragraph style={{ marginBottom: 0 }}>{description}</Paragraph>
        {!hasAvailableSeason ? (
          <Alert
            type="warning"
            showIcon
            title="Your club needs at least one active season roster before it can accept this invitation."
            description="Create or activate a current season roster for this club, then reopen this invitation to finish setup."
          />
        ) : null}
        <div>
          <Text strong>Season roster</Text>
          <Select
            style={{ width: "100%", marginTop: 8 }}
            value={selectedSeasonId ?? undefined}
            onChange={(value) => setSelectedSeasonId(value)}
            loading={loadingOptions}
            disabled={!hasAvailableSeason}
            placeholder="Select a season roster"
            options={clubSeasons.map((clubSeason) => ({
              label: `${clubSeason.seasonName} (${dayjs(clubSeason.startDate).format("MM/DD/YYYY")} - ${dayjs(clubSeason.endDate).format("MM/DD/YYYY")})`,
              value: clubSeason.seasonId,
            }))}
          />
        </div>
        <div>
          <Text strong>Pool</Text>
          <Select
            style={{ width: "100%", marginTop: 8 }}
            value={selectedPoolId ?? undefined}
            onChange={(value) => setSelectedPoolId(value)}
            loading={loadingOptions}
            disabled={!hasAvailableSeason || !hasAvailablePool}
            placeholder="Select a pool"
            options={poolOptions.map((pool) => ({
              label: `${pool.pool_name}${pool.course_label ? ` (${pool.course_label === "CUSTOM" ? "SCY" : pool.course_label})` : ""}${pool.address ? ` - ${pool.address}` : ""}`,
              value: pool.poolId,
            }))}
          />
          {hasAvailableSeason && !hasAvailablePool ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              title="Your club needs a pool before it can accept this invitation."
              description={
                canCreatePoolForInvitationClub ? (
                  <div>
                    <div style={{ marginBottom: 12 }}>
                      Add a pool for {actingClub?.name || actingClub?.name_long || "your club"} now, then select it to finish accepting this invitation.
                    </div>
                    <Button
                      type="primary"
                      onClick={() => setCreatePoolModalVisible(true)}
                      disabled={Boolean(poolCreateBlockedReason)}
                    >
                      Add Pool For {actingClub?.name || actingClub?.name_long || "This Club"}
                    </Button>
                    {poolCreateBlockedReason ? (
                      <div style={{ marginTop: 12 }}>{poolCreateBlockedReason}</div>
                    ) : null}
                  </div>
                ) : (
                  poolCreateBlockedReason ||
                  "Only the acting club can add pools from this invitation flow. Switch to the invited club account context, then add a pool here."
                )
              }
            />
          ) : null}
        </div>
        {selectedSeason ? (
          <Alert
            type="info"
            showIcon
            title={`Confirm Invite & Save My Club Setup`}
          />
        ) : null}
        <div>
          <button
            type="button"
            onClick={() => setInvitedTeamsExpanded((current) => !current)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: 0,
              border: "none",
              background: "none",
              color: "#1677ff",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {invitedTeamsExpanded ? <DownOutlined /> : <RightOutlined />}
            <span>View invited teams ({orderedInvitedTeams.length})</span>
          </button>
          {invitedTeamsExpanded ? (
            <div
              style={{
                marginTop: 10,
                borderRadius: 10,
                border: "1px solid rgba(16, 18, 66, 0.08)",
                background: "rgba(248, 251, 255, 0.9)",
                padding: 12,
              }}
            >
              {invitedTeamsLoading ? (
                <Text type="secondary">Loading invited teams...</Text>
              ) : orderedInvitedTeams.length === 0 ? (
                <Text type="secondary">No invited teams found for this invitation.</Text>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {orderedInvitedTeams.map((team) => {
                    const displayName =
                      team.clubNameLong || team.clubName || `Club ${team.clubId}`
                    const isMyClub = clubId === team.clubId

                    return (
                      <Tag key={`invited_team_${team.virtualMeetInvitationId}`}>
                        {displayName}
                        {isMyClub ? " (My Club)" : ""}
                      </Tag>
                    )
                  })}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </Space>
      <CreateMeetPoolModal
        visible={createPoolModalVisible}
        targetClubId={clubId || 0}
        clubName={actingClub?.name || actingClub?.name_long || "Your Club"}
        onCancel={() => setCreatePoolModalVisible(false)}
        onCreated={async ({ poolId }) => {
          try {
            setLoadingOptions(true)
            const nextOptions = await loadClubSetupOptions()
            const createdPool = nextOptions.pools.find((pool) => pool.poolId === poolId) || null

            if (createdPool) {
              setSelectedPoolId(createdPool.poolId)
            }
          } catch (err) {
            message.error("Problem refreshing pool options for this invitation.")
          } finally {
            setLoadingOptions(false)
            setCreatePoolModalVisible(false)
          }
        }}
      />
    </Modal>
  )
}